import ListToolBarActions from 'components/toolbar/ListToolBarActions';
import { useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import ESpinner from 'components/others/Spinner';
import SpinnerBlock from 'components/others/SpinnerBlock';
import SolarIcon from 'components/icons/SolarIcon';
import EToolTip from 'components/others/EToolTip';
import { useToast } from 'context/ToastContext';
import { useSettings } from 'context/SettingsContext';
import ReportDate from '../ReportDate';
import ReportExportButton from '../ReportExportButton';
import { useReportUsers } from './model';
import { exportUsersReport } from './model/exportUsersReport';
import OffReportFilter from '../OffReportFilter';
import OffReportUsersFilterForm from './OffReportUsersFilterForm';
import ReportUsersSummaryTable from './ReportUsersSummaryTable';
import ReportUsersDetail from './ReportUsersDetail';

const ReportUsers = () => {
    const { t } = useTranslation(['Reports', 'Objects', 'common']);
    const { showToast } = useToast();
    const { localSettings } = useSettings();
    const [exporting, setExporting] = useState(false);
    const {
        users,
        isReporting,
        setDates,
        dateFilter,
        viewMode,
        objectFilter,
        handleDateFilterChange,
        handleViewModeToggle,
        executor,
        applyFilters,
        refreshReport,
        resetReport,
    } = useReportUsers();

    const isDetail = viewMode === 'detail';

    const handleExport = async () => {
        if (!users || exporting) return;
        setExporting(true);
        try {
            const exported = await exportUsersReport({
                users,
                dates: dateFilter.dates,
                period: dateFilter.period,
                viewMode,
                objectFilter,
                rate: Number(localSettings?.RATE?.current ?? 1) || 1,
                t,
            });
            if (!exported) showToast(t('export.empty'), 'warning');
        } catch {
            showToast(t('common:messError'), 'danger');
        } finally {
            setExporting(false);
        }
    };

    return (
        <Row>
            <Col className="col-sticky" sm={12}>
                <div className="w-100 d-sm-flex pt-1 justify-content-between align-items-start gap-2">
                    <div className="flex-shrink-0">
                        <h4 className="p-0 m-0 mb-0 me-2 text-nowrap">
                            {t('common:SideBarUl.itemRepostsUsers')}
                        </h4>
                    </div>
                    <div className="mt-2 mt-sm-0 mb-2 page-toolbar-actions">
                        <ReportExportButton
                            disabled={!users || isReporting || exporting}
                            onClick={() => {
                                void handleExport();
                            }}
                        />
                        <EToolTip
                            title={
                                isDetail
                                    ? t('usersView.summaryHint')
                                    : t('usersView.detailHint')
                            }
                        >
                            <span className="d-inline-block">
                                <Button
                                    className="fs-14 px-2 toolbar-icon-btn"
                                    size="sm"
                                    variant={isDetail ? 'info' : 'secondary'}
                                    disabled={isReporting}
                                    onClick={(e) => {
                                        e.currentTarget.blur();
                                        handleViewModeToggle();
                                    }}
                                >
                                    <SolarIcon name="list-ul" className="fs-14 p-0" />
                                </Button>
                            </span>
                        </EToolTip>
                        <ReportDate
                            setDates={setDates}
                            disabled={isReporting}
                            initialFilter={dateFilter}
                            onFilterChange={handleDateFilterChange}
                        />
                        <ListToolBarActions onRefresh={refreshReport} onReset={resetReport} disabled={isReporting} />
                        <OffReportFilter
                            values={{ objectFilter, executor }}
                            defaults={{ objectFilter: 'all', executor: null }}
                            onApply={applyFilters}
                            active={objectFilter !== 'all' || !!executor}
                            disabled={isReporting}
                        >
                            <OffReportUsersFilterForm />
                        </OffReportFilter>
                    </div>
                </div>
            </Col>

            <Col sm={12}>
                <div className="px-0 position-relative">
                    {users == null ? (
                        <ESpinner />
                    ) : (
                        <>
                            <SpinnerBlock onlyBlur={true} show={isReporting} />
                            {isDetail ? (
                                <ReportUsersDetail users={users} />
                            ) : (
                                <ReportUsersSummaryTable users={users} />
                            )}
                        </>
                    )}
                </div>
            </Col>
        </Row>
    );
};

export default ReportUsers;
